import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/economy/stats").then(({ data }) => setS(data)); }, []);
  if (!s) return <Loader />;
  return (
    <div className="space-y-4 fade-up">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card label="Total Accounts" v={s.total_accounts} />
        <Card label="Total Currency" v={s.total_currency.toLocaleString()} highlight />
        <Card label="Avg Balance" v={s.avg_balance} />
        <Card label="Max Balance" v={s.max_balance.toLocaleString()} />
        <Card label="Total Pals Owned" v={s.total_pals_owned.toLocaleString()} />
      </div>
      <AdminFeed />
    </div>
  );
}


function AdminFeed() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [random, setRandom] = useState({ open: false, channel_id: "", duration_min: 30, busy: false });
  const load = () => api.get("/admin/feed?limit=20").then(({ data }) => setItems(data.items || [])).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  const spawnRandom = async () => {
    if (!random.channel_id) return toast.error("Channel ID required");
    setRandom((r) => ({ ...r, busy: true }));
    try {
      const { data } = await api.post("/admin/giveaways/random", {
        channel_id: random.channel_id,
        duration_min: parseInt(random.duration_min, 10) || 30,
      });
      toast.success(`Queued — ${data.amount} 🪙 for ${data.duration_min} min (request #${data.request_id})`);
      setRandom({ open: false, channel_id: random.channel_id, duration_min: 30, busy: false });
      setTimeout(load, 2000);
    } catch (e) {
      toast.error(apiError(e));
      setRandom((r) => ({ ...r, busy: false }));
    }
  };
  const kindColor = (k) => ({
    giveaway: "text-[#FFD700]",
    random_giveaway: "text-[#FFD700]",
    perm_admin: "text-[var(--danger)]",
    schedule: "text-[var(--accent)]",
    report: "text-[var(--warning)]",
    hunt: "text-[var(--teal)]",
    server_config: "text-[var(--accent)]",
  }[k] || "text-[var(--muted)]");
  return (
    <div className="glass-card p-5" data-testid="admin-feed">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="label !mb-0">🔔 Admin Action Feed</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRandom((r) => ({ ...r, open: !r.open }))}
            className="btn btn-sm"
            data-testid="random-giveaway-open"
            title="Queue a one-click surprise giveaway (50–250 🪙, 1 winner)"
          >
            🎁 Spawn Random Giveaway
          </button>
          <span className="text-[10px] text-[var(--muted)]">last 20 · auto-refresh 30s</span>
        </div>
      </div>

      {random.open && (
        <div className="bg-black/30 rounded p-3 mb-3 grid sm:grid-cols-3 gap-2 items-end" data-testid="random-giveaway-form">
          <Field label="Channel ID">
            <input
              className="input font-mono text-xs"
              value={random.channel_id}
              onChange={(e) => setRandom({ ...random, channel_id: e.target.value })}
              placeholder="139…"
              data-testid="random-giveaway-channel"
            />
          </Field>
          <Field label="Duration (min)">
            <input
              className="input"
              type="number"
              min="1"
              max="720"
              value={random.duration_min}
              onChange={(e) => setRandom({ ...random, duration_min: e.target.value })}
              data-testid="random-giveaway-duration"
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={spawnRandom}
              className="btn"
              disabled={random.busy || !random.channel_id}
              data-testid="random-giveaway-submit"
            >
              {random.busy ? "Queueing…" : "🎲 Roll & Queue"}
            </button>
            <button
              onClick={() => setRandom({ ...random, open: false })}
              className="btn btn-secondary"
              data-testid="random-giveaway-cancel"
            >
              Cancel
            </button>
          </div>
          <p className="text-[10px] text-[var(--muted)] sm:col-span-3 -mt-1">
            Rolls 50–250 🪙 (in 25-skull increments), 1 winner. The bot posts the Discord embed within ~20s and handles entry + auto-payout.
          </p>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-[var(--muted)]">No recent activity.</p>
      ) : (
        <ul className="divide-y divide-white/5 max-h-96 overflow-y-auto">
          {items.map((it, i) => (
            <li key={i} className="py-2 flex items-start gap-3 text-sm" data-testid={`feed-item-${i}`}>
              <span className={`badge ${kindColor(it.kind)} border-white/10 flex-shrink-0`}>
                {it.kind}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{it.title}</div>
                {it.detail && <div className="text-[11px] text-[var(--muted)] truncate">{it.detail}</div>}
              </div>
              <div className="text-[10px] text-[var(--muted)] flex-shrink-0 text-right">
                {it.timestamp ? new Date(it.timestamp).toLocaleString() : ""}
                {it.actor && <div className="font-mono">{String(it.actor).slice(0, 14)}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function Card({ label, v, highlight }) {
  return (
    <div className="glass-card p-5">
      <div className="label">{label}</div>
      <div className={`text-2xl mt-1 ${highlight ? "glow-gold" : "glow-teal"}`}>{v}</div>
    </div>
  );
}
